//================= [JS] ======================================
import {ref, shallowRef} from 'vue';
import {callOpen} from '@/composables/individualCoaching/coachingFormat';
import type {ISessionOccurrence} from '@/types/components/individualCoaching/IIndividualCoaching';

/** What it takes to show a 1:1 call anywhere in the app. */
export interface ActiveCall {
  /** The coachee's id, or `me` in the coachee's own room. */
  clientId: string;
  occurrence: ISessionOccurrence;
  peerName: string;
  isCoach: boolean;
}

/** T00IILDDEQF2 — per tab, so a second tab never takes over someone's call. */
export const ACTIVE_CALL_STORAGE_KEY = 'cc.individualCoaching.activeCall';

function remember(call: ActiveCall | null): void {
  try {
    if (call) {
      sessionStorage.setItem(ACTIVE_CALL_STORAGE_KEY, JSON.stringify(call));
    } else {
      sessionStorage.removeItem(ACTIVE_CALL_STORAGE_KEY);
    }
  } catch {
    // Without storage the call just cannot be resumed after a reload.
  }
}

/** The call this tab ran before a reload, if its window is still open. */
export function rememberedCall(now: Date = new Date()): ActiveCall | null {
  try {
    const stored = sessionStorage.getItem(ACTIVE_CALL_STORAGE_KEY);
    const call = stored ? JSON.parse(stored) as ActiveCall : null;
    if (call?.occurrence && callOpen(call.occurrence.start, call.occurrence.end, now)) {
      return call;
    }
    sessionStorage.removeItem(ACTIVE_CALL_STORAGE_KEY);
  } catch {
    // A broken entry is simply forgotten.
  }
  return null;
}

// One call per tab, shared by every component: module state, not per instance.
const active = shallowRef<ActiveCall | null>(null);
/** T00IILDDEQF2 — a call cut off by a reload, waiting for "resume". */
const interrupted = shallowRef<ActiveCall | null>(rememberedCall());
/** Counts finished calls, so a room can reload sessions and recordings afterwards. */
const ended = ref(0);
/** Set by `resume`: the call joins right away instead of showing the preview. */
const resumed = ref(false);

/**
 * T009OSKBRBOH — the running 1:1 call lives above the pages. The room only
 * starts it; `CoachingCallHost` in the layout shows it, so moving to files or
 * modules keeps the connection, the chat and a recording going.
 *
 * T00IILDDEQF2 — a reload cannot keep a WebRTC connection, but the tab
 * remembers the call and offers to join again with one click.
 */
export function useActiveCall() {
  function open(call: ActiveCall, rejoin = false): void {
    interrupted.value = null;
    resumed.value = rejoin;
    active.value = call;
    remember(call);
  }

  /** The call ended (hung up, or the host went away). */
  function close(): void {
    remember(null);
    if (active.value) {
      active.value = null;
      ended.value += 1;
    }
  }

  /** Hung up but still on the end screen: a reload must not offer the call again. */
  function forget(): void {
    remember(null);
  }

  /** After a reload: join the interrupted call again. */
  function resume(): void {
    if (interrupted.value) {
      open(interrupted.value, true);
    }
  }

  /** After a reload: do not go back into the call. */
  function dismiss(): void {
    interrupted.value = null;
    remember(null);
  }

  return {active, interrupted, resumed, ended, open, close, forget, resume, dismiss};
}
